import {Palette} from 'hubbl-shared';
import moment from 'moment';
import {memo, useMemo, useCallback} from 'react';
import styled from 'styled-components';
import {EventDTO, HubblGroupDTO, HubblBreakDTO} from '../../models';
import {
  EventCard,
  HubblGroupCard,
  HubblConnectCard,
} from '../social-activity-cards';
import {Timestamp} from 'firebase/firestore';

export type SocialActivity =
  | {type: 'event'; item: EventDTO}
  | {type: 'group'; item: HubblGroupDTO}
  | {type: 'connect'; item: HubblBreakDTO};

type TimelineDate = {
  day: string;
  month: string;
  year: number;
  isToday: boolean;
  isPast: boolean;
};

type GroupedActivities = Record<
  string,
  {
    date: TimelineDate;
    activities: SocialActivity[];
  }
>;

interface SocialActivitiesTimelineProps {
  activities: SocialActivity[];
  onEventClick: (eventId: string) => void;
}

export const SocialActivitiesTimeline = memo(
  ({activities, onEventClick}: SocialActivitiesTimelineProps) => {
    const formatTimelineDate = useCallback((date: Timestamp) => {
      const activityDate = date.toDate();
      return {
        day: moment(activityDate).format('DD'),
        month: moment(activityDate).format('MMM'),
        year: activityDate.getFullYear(),
        isToday: moment(activityDate).isSame(moment(), 'day'),
        isPast: moment(activityDate).isBefore(moment(), 'day'),
      };
    }, []);

    const handleEventClick = useCallback(
      (eventId: string) => {
        onEventClick(eventId);
      },
      [onEventClick],
    );

    const memoizedActivities = useMemo(() => {
      // Group activities by date
      const groupedActivities = activities.reduce((groups, activity) => {
        let startDateTime: Timestamp;
        let activityId: string;

        // Get start date based on activity type
        switch (activity.type) {
          case 'event':
            startDateTime = activity.item.startDateTime;
            activityId = activity.item.id;
            break;
          case 'group':
            startDateTime = activity.item.startDateTime;
            activityId = activity.item.id;
            break;
          case 'connect':
            startDateTime = activity.item.startDateTime;
            activityId = activity.item.id;
            break;
        }

        const timelineDate = formatTimelineDate(startDateTime);
        const dateKey = `${timelineDate.year}-${timelineDate.month}-${timelineDate.day}`;

        if (!groups[dateKey]) {
          groups[dateKey] = {
            date: timelineDate,
            activities: [],
          };
        }

        groups[dateKey].activities.push(activity);
        return groups;
      }, {} as GroupedActivities);

      // Convert grouped activities to timeline items
      return Object.values(groupedActivities)
        .map((group, groupIndex) => {
          return group.activities.map((activity, activityIndex) => {
            const isFirstActivityOfDay = activityIndex === 0;
            const isPast = group.date.isPast;

            return (
              <TimelineItem key={`${activity.type}-${activity.item.id}`}>
                {isFirstActivityOfDay && (
                  <TimelineDate>
                    <DateCircle
                      isToday={group.date.isToday}
                      isPast={group.date.isPast}>
                      <DateDay>{group.date.day}</DateDay>
                      <DateMonth>{group.date.month}</DateMonth>
                    </DateCircle>
                  </TimelineDate>
                )}

                {!isFirstActivityOfDay && <TimelineDateSpacer />}

                {activity.type === 'event' && (
                  <EventCard
                    event={activity.item}
                    isPast={isPast}
                    onClick={() => handleEventClick(activity.item.id)}
                  />
                )}

                {activity.type === 'group' && (
                  <HubblGroupCard hubblGroup={activity.item} isPast={isPast} />
                )}

                {activity.type === 'connect' && (
                  <HubblConnectCard
                    hubblConnect={activity.item}
                    isPast={isPast}
                  />
                )}
              </TimelineItem>
            );
          });
        })
        .flat();
    }, [activities, formatTimelineDate, handleEventClick]);

    if (activities.length === 0) {
      return null;
    }

    return (
      <div style={{width: '90%'}}>
        <TimelineContainer>
          {/* Single timeline line behind all circles */}
          <TimelineLine />
          {memoizedActivities}
        </TimelineContainer>
      </div>
    );
  },
);

const TimelineContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  position: relative;
  padding-bottom: 2rem;
  overflow: visible;
  width: 100%;
  min-width: 0;

  @media (max-width: 768px) {
    gap: 1rem;
    padding-bottom: 1rem;
  }
`;

const TimelineItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  position: relative;
  width: 100%;
  min-width: 0;

  @media (max-width: 768px) {
    gap: 1rem;
    flex-direction: column;
    align-items: flex-start;
  }
`;

const TimelineDate = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 80px;
  position: relative;
  justify-content: center;

  @media (max-width: 768px) {
    min-width: 60px;
    align-self: flex-start;
  }
`;

const TimelineDateSpacer = styled.div`
  min-width: 80px;
  position: relative;

  @media (max-width: 768px) {
    min-width: 60px;
  }
`;

const DateCircle = styled.div<{isToday: boolean; isPast: boolean}>`
  width: 60px;
  height: 60px;
  border-radius: 24px;
  background: ${props => {
    if (props.isToday) return Palette.primaryDustySky;
    if (props.isPast) return Palette.almostBlack250;
    return Palette.white;
  }};
  color: ${Palette.almostBlack};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  position: relative;
  z-index: 2;

  @media (max-width: 768px) {
    width: 50px;
    height: 50px;
    border-radius: 20px;
  }
`;

const DateDay = styled.div`
  font-size: 1.25rem;
  line-height: 1;
  margin-bottom: -2px;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const DateMonth = styled.div`
  font-size: 0.75rem;
  line-height: 1;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  @media (max-width: 768px) {
    font-size: 0.65rem;
  }
`;

const TimelineLine = styled.div`
  position: absolute;
  top: 30px;
  left: 40px;
  width: 3px;
  height: calc(100% - 90px);
  background: ${Palette.almostBlackTransparent10};
  z-index: 0;
  border-radius: 2px;
  pointer-events: none;

  @media (max-width: 768px) {
    top: 25px;
    left: 30px;
    height: calc(100% - 70px);
  }
`;
